import { IUser } from './../models/IUser/IUser';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { userApi } from '../services/user';
import { RootState } from './store';
import { ICompany } from '../models/ICompany/ICompany';
import { authApi } from '../services/auth';
import { LocalStorageKeys, localStorageHelper } from '../helpers/localStorage.helper';

export type UserRole =
  | 'owner'
  | 'super_admin'  
  | 'noam'
  | 'admin'
  | 'agent'
  | 'manager'
  | 'driver'
  | 'tiful'
  | 'office_order'
  | 'client'
  | 'none';
export interface IUserState {
  info: IUser;
  company: ICompany | null;
  isNeedChangePassword: boolean;
  isLoading: boolean;
  error: string;
  notificationListener: boolean;
  driverReceives?: string;
}

const initialState: IUserState = {
  info: {
    role: 'none',
    roles: [],
    api_token: '',
    accessToken: '',
    refreshToken: '',
    email: '',
    fullName: '',
    name: '',
    firstName: '',
    sureName: '',
    avatar: '',
    profile_type: '',
    profile_id: 0,
    unread_notifications: 0,
  },
  isNeedChangePassword: false,
  company: null,
  isLoading: false,
  notificationListener: false,
  error: '',
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<IUser>) => {
      state.info = action.payload;
    },
    setCompany: (state, action: PayloadAction<ICompany>) => {
      state.company = action.payload;
    },
    setDriverReceives: (state, action: PayloadAction<string>) => {
      state.driverReceives = action.payload;
    },
    setUserRole: (state, action: PayloadAction<UserRole>) => {
      state.info.role = action.payload;
    },
    setUserEmail: (state, action: PayloadAction<string>) => {
      state.info.email = action.payload;
    },
    logout: (state) => {
      // Clear order draft from localStorage on logout
      localStorageHelper.remove(LocalStorageKeys.ORDER_FLOW_DRAFT);
      console.log('✅ Order draft cleared on logout (Redux action)');
      
      state.info = initialState.info;
    },
    connectMessaging: () => {},
    onNotificationMessage: (state) => {
      state.notificationListener = !state.notificationListener;
      // state.info = initialState.info;
    },
  },
  extraReducers: (builder) => {
    //LOGIN RESPONSE DATA
    builder
      .addMatcher(
        authApi.endpoints.login.matchFulfilled,
        (state, { payload }) => {
          state.info = payload.user;
          state.info.accessToken = payload.accessToken;
          state.info.refreshToken = payload.refreshToken;
          state.info.name = `${payload.user.firstName || ''} ${
            payload.user.lastName || ''
          }`;
          state.company = payload.user['company'];
        }
      )
      .addMatcher(
        authApi.endpoints.loginAsClient.matchFulfilled,
        (state, { payload }) => {
          if (!payload.accessToken) return;
          
          state.info = payload.user;
          state.info.accessToken = payload.accessToken;
          state.info.refreshToken = payload.refreshToken;
          state.info.name = `${payload.user.firstName || ''} ${
            payload.user.lastName || ''
          }`;
          state.company = payload.user['company'];
        }
      )
      .addMatcher(
        userApi.endpoints.getUser.matchFulfilled,
        (state, { payload }) => {
          const _accessToken = state.info.accessToken;
          const _refreshToken = state.info.refreshToken;

          state.info = payload;
          state.info.accessToken = _accessToken;
          state.info.refreshToken = _refreshToken;
          // Ensure imageUrl is set from meta.imageAsBase64 if not present
          state.info.imageUrl = payload.imageUrl || payload.meta?.imageAsBase64 || state.info.imageUrl;
          state.company = payload['company'];
        }
      )
      .addMatcher(
        userApi.endpoints.updateProfile.matchFulfilled,
        (state, { payload }) => {
          if (!payload) return;
          
          const _accessToken = state.info.accessToken;
          const _refreshToken = state.info.refreshToken;
          const _role = state.info.role;

          state.info = {
            ...state.info,
            ...payload,
            accessToken: _accessToken,
            refreshToken: _refreshToken,
            role: _role,
            name: `${payload.firstName || ''} ${payload.lastName || ''}`.trim(),
            imageUrl: payload.imageUrl || payload.meta?.imageAsBase64 || payload.avatar || state.info.imageUrl,
          };
        }
      )
      .addMatcher(
        userApi.endpoints.updateProfileFormData.matchFulfilled,
        (state, { payload }) => {
          if (!payload) return;
          
          const _accessToken = state.info.accessToken;
          const _refreshToken = state.info.refreshToken;
          const _role = state.info.role;

          state.info = {
            ...state.info,
            ...payload,
            accessToken: _accessToken,
            refreshToken: _refreshToken,
            role: _role,
            name: `${payload.firstName || ''} ${payload.lastName || ''}`.trim(),
            imageUrl: payload.imageUrl || payload.meta?.imageAsBase64 || payload.avatar || state.info.imageUrl,
          };
        }
      )
      .addMatcher(
        authApi.endpoints.signUpComplete.matchFulfilled,
        (state, { payload }) => {
          if (!payload.company.status || payload.company.status === 'draft')
            return;

          const { accessToken, user } = payload;

          state.info = user;
          state.info.name = `${payload.user.firstName || ''} ${
            payload.user.lastName || ''
          }`;
          state.info.role = user.role || 'owner'; // Use role from backend (owner, manager, etc.)
          state.info.accessToken = accessToken;
          state.company = user.company;
        }
      )
      .addMatcher(
        authApi.endpoints.switchAccount.matchFulfilled,
        (state, { payload }) => {
          state.info = payload.user;
          state.info.accessToken = payload.accessToken;
          state.info.name = `${payload.user.firstName || ''} ${
            payload.user.lastName || ''
          }`;
          state.company = payload.user['company'];
        }
      )
      .addMatcher(
        authApi.endpoints.switchCompany.matchFulfilled,
        (state, { payload }) => {
          state.info = payload.user;
          state.info.accessToken = payload.accessToken;
          state.info.name = `${payload.user.firstName || ''} ${
            payload.user.lastName || ''
          }`;
          state.company = payload.user['company'];
        }
      );
  },
});

export const selectUser = (state: RootState) => state.user.info.role;
export const selectNotificationListener = (state: RootState) =>
  state.user.notificationListener;
export const selectProfile = (state: RootState) => state.user.info;
export const selectProfileID = (state: RootState) => state.user.info.profile_id;
export const selectCompany = (state: RootState) => state.user.company;
export const selectCompanyType = (state: RootState) => state.user.company?.companyType;
export const selectDriverReceives = (state: RootState) =>
  state.user.driverReceives;

export const {
  setUser,
  setCompany,
  setUserRole,
  logout,
  setDriverReceives,
  connectMessaging,
  setUserEmail,
  onNotificationMessage,
} = userSlice.actions;

export default userSlice.reducer;
